import {
  Button,
  Divider,
  Grid,
  Typography,
  Box,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import ServerMessageSnackbar from "../../../reusableComponents/serverMessageSnackbar/serverMessageSnackbar";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import TableTemplate from "../../../reusableComponents/tableTemplate/tableTemplate";
import UserDialog from "./userDialog/userDialog";
import CancelSubscriptionDialog from "./cancelSubscriptionDialog/cancelSubscriptionDialog";
import {
  disableUser,
  enableUser,
  getUsers,
} from "../../../../store/thunks/systemManagementThunk";
import { filterAutofillGuard } from "../../../../utils/tableUtils";
import { MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import LockResetIcon from "@mui/icons-material/LockReset";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ConfirmUserActionDialog from "../../../reusableComponents/confirmUserActionDialog/confirmUserActionDialog";
import {
  logoutUser,
  resetPasswordLink,
} from "../../../../store/thunks/authenticationThunk";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { logout } from "../../../../store/features/authentication/authenticationSlice";
import { useNavigate } from "react-router-dom";
import { portalManagementReset } from "../../../../store/features/portalManagement/portalManagementSlice";
import { systemManagementReset } from "../../../../store/features/systemManagement/systemManagementSlice";
import Spinner from "../../../reusableComponents/spinner/spinner";
import AttachSubscriptionDialog from "../../../reusableComponents/attachSubscriptionDialog/attachSubscriptionDialog";
import {
  exportToExcel,
  userFullName,
  withUserName,
} from "../../../../utils/utils";

const UserManagement = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { allUsers, getUserLoading } = useAppSelector(
    (state: any) => state.systemManagement
  );
  const { user, resetPasswordLinkLoading } = useAppSelector(
    (state: any) => state.authentication
  );
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);

  const [openEditUserDialog, setOpenEditUserDialog] = useState(false);
  const [editUserRowData, setEditUserRowData] = useState<any>();

  const [openDisableUserDialog, setOpenDisableUserDialog] = useState(false);
  const [openEnableUserDialog, setOpenEnableUserDialog] = useState(false);
  const [disableUserRowData, setDisableUserRowData] = useState<any>();

  const [openAttachSubscriptionDialog, setOpenAttachSubscriptionDialog] =
    useState(false);
  const [attachSubscriptionData, setAttachSubscriptionData] = useState<any>();

  const [openCancelSubscriptionDialog, setOpenCancelSubscriptionDialog] =
    useState(false);
  const [CancelSubscriptionRowData, setCancelSubscriptionRowData] =
    useState<any>();

  const [selectedUserNavigation, setSelectedUserNavigation] =
    useState("active_user");


  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const {
    data: { data = [] } = {}, //your data and api response will probably be different
    isError,
    isRefetching,
    isLoading,
    refetch,
  } = useQuery<any>({
    queryKey: ["table-data"],
    queryFn: async () => {
      const result: any = await dispatch(getUsers({}));
      if (result?.error?.message === "Rejected") {
        dispatch(logoutUser({ user_index: user?.client_user_index }));
        dispatch(portalManagementReset());
        dispatch(systemManagementReset());
        dispatch(logout());
        navigate("/login");
      }
      // The rows come from the store, not from here. This used to also set them
      // from its own payload while an effect set them from `allUsers`, so two
      // writers raced and whichever landed last won. It also read
      // `.payload.all_users` unguarded, which throws on a rejected thunk.
      return result?.payload?.all_users ?? [];
    },
    placeholderData: keepPreviousData
  });

  const userColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: "Table",
        header: "",
        columns: [
          {
            accessorFn: (row: any) => `${row.first_name} ${row.last_name}`, //accessorFn used to join multiple data into a single cell
            id: "name", //id is still required when using accessorFn instead of accessorKey
            header: "Full Name",
            Cell: ({ renderedCellValue, row }) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <span>{renderedCellValue}</span>
              </Box>
            ),
          },
          {
            accessorKey: "user_name", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Email",
          },
          {
            accessorKey: "role", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Role",
          },
          {
            accessorKey: "customer_name", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Clients",
          },
          {
            accessorKey: "client_type_name", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Client Type",
          },
          {
            accessorKey: "client_title", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "User Title",
          },
          {
            accessorKey: "client_branch", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Client Branch",
          },
          {
            accessorKey: "client_department", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Client Department",
          },
          {
            accessorKey: "salemen_name", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Sale Rep",
          },
          {
            accessorKey: "login_status", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Logged In",
          },
          {
            accessorKey: "last_login", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Last Login",
          },
          {
            accessorKey: "login_count",
            header: "Login Count",
          },
        ],
      },
      // {
      //   id: "Full Name", //id used to define `group` column
      //   header: "",
      //   columns: [
      //     {
      //       accessorFn: (row) => `${row.firstName} ${row.lastName}`, //accessorFn used to join multiple data into a single cell
      //       id: "name", //id is still required when using accessorFn instead of accessorKey
      //       header: "Name",
      //       size: 250,
      //       Cell: ({ renderedCellValue, row }) => (
      //         <Box
      //           sx={{
      //             display: "flex",
      //             alignItems: "center",
      //             gap: "1rem",
      //           }}
      //         >
      //           <span>{renderedCellValue}</span>
      //         </Box>
      //       ),
      //     },
      //     {
      //       accessorKey: "email", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
      //       enableClickToCopy: true,
      //       filterVariant: "autocomplete",
      //       header: "Email",
      //       size: 300,
      //     },
      //   ],
      // },
      // {
      //   id: "id",
      //   header: "",
      //   columns: [
      //     {
      //       accessorKey: "salary",
      //       // filterVariant: 'range', //if not using filter modes feature, use this instead of filterFn
      //       filterFn: "between",
      //       header: "Salary",
      //       size: 200,
      //       //custom conditional format and styling
      //       Cell: ({ cell }) => (
      //         <Box
      //           component="span"
      //           sx={(theme) => ({
      //             backgroundColor:
      //               cell.getValue<number>() < 50_000
      //                 ? theme.palette.error.dark
      //                 : cell.getValue<number>() >= 50_000 &&
      //                   cell.getValue<number>() < 75_000
      //                 ? theme.palette.warning.dark
      //                 : theme.palette.success.dark,
      //             borderRadius: "0.25rem",
      //             color: "#fff",
      //             maxWidth: "9ch",
      //             p: "0.25rem",
      //           })}
      //         >
      //           {cell.getValue<number>()?.toLocaleString?.("en-US", {
      //             style: "currency",
      //             currency: "USD",
      //             minimumFractionDigits: 0,
      //             maximumFractionDigits: 0,
      //           })}
      //         </Box>
      //       ),
      //     },
      //     {
      //       accessorKey: "jobTitle", //hey a simple column for once
      //       header: "Job Title",
      //       size: 350,
      //     },
      //     {
      //       accessorFn: (row) => new Date(row.startDate), //convert to Date for sorting and filtering
      //       id: "startDate",
      //       header: "Start Date",
      //       filterVariant: "date",
      //       filterFn: "lessThan",
      //       sortingFn: "datetime",
      //       Cell: ({ cell }) => cell.getValue<Date>()?.toLocaleDateString(), //render Date as a string
      //       Header: ({ column }) => <em>{column.columnDef.header}</em>, //custom header markup
      //       muiFilterTextFieldProps: {
      //         sx: {
      //           minWidth: "250px",
      //         },
      //       },
      //     },
      //   ],
      // },
    ],
    []
  );

  // The one place the visible rows are decided: the store's list, filtered by
  // the chosen tab. Derived rather than stored, so a fetch that fails or races
  // cannot leave the table showing an empty array it can never recover from.
  const userList = useMemo(() => {
    // The backend's scoped-admin branch (AdminService._list_users_scoped) joins
    // through users_clients, which holds one row per scope attachment — so an
    // Admin/Account Admin saw some training users three times. Deduped there
    // too, but keyed here on identity so the table is immune whichever backend
    // version answers. First occurrence wins; the fanned-out rows are identical.
    // No iterator spread: the ES5 target rejects it (TS2802).
    const byIdentity = new Map<any, any>();
    (allUsers ?? []).forEach((u: any) => {
      if (!byIdentity.has(u.client_user_index)) byIdentity.set(u.client_user_index, u);
    });
    const rows: any[] = Array.from(byIdentity.values());
    return selectedUserNavigation === "active_user"
      ? rows.filter((user: any) => user.status === "Active")
      : rows.filter((user: any) => user.status !== "Active");
  }, [allUsers, selectedUserNavigation]);

  const userTable = useMaterialReactTable({
    muiFilterTextFieldProps: filterAutofillGuard,
    columns: userColumns,
    data: userList,
    state: {
      isLoading: getUserLoading,
    },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageIndex: 0, pageSize: 10 },
      columnPinning: {
        left: ["mrt-row-expand", "mrt-row-select"],
        right: ["mrt-row-actions"],
      },
    },
    positionToolbarAlertBanner: "bottom",
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() =>
            exportToExcel(
              table.getPrePaginationRowModel().rows,
              "Zintoro Users"
            )
          }
          startIcon={<FileDownloadIcon />}
        >
          Export All Rows
        </Button>
      </Box>
    ),

    renderRowActionMenuItems: ({ closeMenu, row }) => [
      selectedUserNavigation === "active_user" && (
        <MenuItem
          key={0}
          onClick={() => {
            handleOpenEditUser(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Edit User
        </MenuItem>
      ),
      selectedUserNavigation === "active_user" && (
        <MenuItem
          key={1}
          onClick={() => {
            handleDisableUser(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          Disable User
        </MenuItem>
      ),
      selectedUserNavigation === "active_user" &&
        user?.role_index === 100 && (
          <MenuItem
            key={2}
            onClick={() => {
              handleOpenAttachSubscription(row.original);
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <UnsubscribeIcon />
            </ListItemIcon>
            Attach Subscription
          </MenuItem>
        ),
      selectedUserNavigation === "active_user" &&
        user?.role_index === 100 &&
        row?.original?.subscriptions?.length > 0 && (
          <MenuItem
            key={2}
            onClick={() => {
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <UnsubscribeIcon />
            </ListItemIcon>
            Edit Subscription
          </MenuItem>
        ),
      selectedUserNavigation === "active_user" && (
        <MenuItem
          key={3}
          onClick={() => {
            handleResetPassword(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <LockResetIcon />
          </ListItemIcon>
          Reset Password
        </MenuItem>
      ),
      selectedUserNavigation !== "active_user" && (
        <MenuItem
          key={4}
          onClick={() => {
            handleEnableUser(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Enable User
        </MenuItem>
      ),
    ]
  });

  const handleUserNavigation = (active_user: any) =>
    setSelectedUserNavigation(active_user);

  const handleOpenCreateUser = () => {
    setOpenCreateUserDialog(true);
  };
  const handleCloseCreateUser = (message: string) => {
    if (message !== "") {
      setSnackbarMessage(withUserName(message));
      setOpenSnackbar(true);
      refetch();
    }
    setOpenCreateUserDialog(false);
  };

  const handleOpenEditUser = (rowData: any) => {
    setEditUserRowData(rowData);
    setOpenEditUserDialog(true);
  };
  const handleCloseEditUser = (message: string) => {
    if (message !== "") {
      setSnackbarMessage(withUserName(message, userFullName(editUserRowData)));
      setOpenSnackbar(true);
      refetch();
    }
    setEditUserRowData(null);
    setOpenEditUserDialog(false);
  };

  const handleDisableUser = (rowData: any) => {
    setDisableUserRowData(rowData);
    setOpenDisableUserDialog(true);
  };
  const handleCloseDisableUser = (message: string) => {
    if (message !== "") {
      setSnackbarMessage(withUserName(message, userFullName(disableUserRowData)));
      setOpenSnackbar(true);
      refetch();
    }
    setDisableUserRowData(null);
    setOpenDisableUserDialog(false);
  };

  const handleEnableUser = (rowData: any) => {
    setDisableUserRowData(rowData);
    setOpenEnableUserDialog(true);
  };
  const handleCloseEnableUser = (message: string) => {
    if (message !== "") {
      setSnackbarMessage(withUserName(message, userFullName(disableUserRowData)));
      setOpenSnackbar(true);
      refetch();
    }
    setDisableUserRowData(null);
    setOpenEnableUserDialog(false);
  };

  const handleOpenCancelSubscription = (rowData: any) => {
    setCancelSubscriptionRowData(rowData);
    setOpenCancelSubscriptionDialog(true);
  };
  const handleCloseCancelSubscription = () => {
    setCancelSubscriptionRowData(null);
    setOpenCancelSubscriptionDialog(false);
  };

  const handleResetPassword = (rowData: any) => {
    dispatch(resetPasswordLink({ username: rowData.user_name })).then(
      (resetPasswordLinkResult: any) => {
        setSnackbarMessage(withUserName(resetPasswordLinkResult.payload.message));
        setOpenSnackbar(true);
      }
    );
  };

  const handleOpenAttachSubscription = (rowData: any) => {
    setAttachSubscriptionData(rowData);
    setOpenAttachSubscriptionDialog(true);
  };
  const handleCloseAttachSubscription = (message: string) => {
    if (message !== "") {
      setSnackbarMessage(withUserName(message));
      setOpenSnackbar(true);
      refetch();
    }
    setAttachSubscriptionData(null);
    setOpenAttachSubscriptionDialog(false);
  };

  return (
    <Grid>
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 20px 10px 20px",
        }}
      >
        <Typography variant="h4" sx={{ color: "#004680", fontWeight: "bold" }}>
          User Management
        </Typography>
      </Grid>
      <Spinner loading={resetPasswordLinkLoading} />
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0px 20px 10px 20px !important",
        }}
      >
        <Grid sx={{ justifyContent: "center", padding: "5px" }}>
          <Button
            onClick={() => handleOpenCreateUser()}
            variant="outlined"
            sx={{
              color: "white !important",
              fontWeight: "bold !important",
              padding: "0px 20px 0px 20px !important",
              backgroundColor: "#38B54A !important",
            }}
          >
            Add User
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          padding: "0px 20px 20px 20px",
        }}
      >
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            backgroundColor: "#4C4C4C !important",
            borderTop: "5px solid white !important",
            height: "40px",
            paddingLeft: "10px",
          }}
        >
          <Button
            onClick={() => {
              handleUserNavigation("active_user");
            }}
            sx={{
              color:
                selectedUserNavigation === "active_user"
                  ? "#38B54A !important"
                  : "white !important",
              padding: "0px 20px 0px 20px !important",
            }}
          >
            Active Users
          </Button>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ color: "white", backgroundColor: "white" }}
          />
          <Button
            onClick={() => {
              handleUserNavigation("disabled_user");
            }}
            sx={{
              color:
                selectedUserNavigation === "disabled_user"
                  ? "#38B54A !important"
                  : "white !important",
              padding: "0px 20px 0px 20px !important",
            }}
          >
            Disabled Users
          </Button>
        </Grid>
        <Grid container item xs={12} sx={{ padding: "20px 0px 20px 0px" }}>
          <TableTemplate tableData={userTable} />
        </Grid>
      </Grid>
      <UserDialog
        mode="create"
        open={openCreateUserDialog}
        onClose={(message?: string) => handleCloseCreateUser(message ?? "")}
      />
      <UserDialog
        mode="edit"
        open={openEditUserDialog}
        onClose={(message?: string) => handleCloseEditUser(message ?? "")}
        editUserData={editUserRowData}
      />
      <ConfirmUserActionDialog
        open={openDisableUserDialog}
        title="Disable User"
        verb="disable"
        user={disableUserRowData}
        onConfirm={() =>
          dispatch(disableUser({ client_user_index: disableUserRowData?.client_user_index }))
        }
        onClose={handleCloseDisableUser}
      />
      <ConfirmUserActionDialog
        open={openEnableUserDialog}
        title="Enable User"
        verb="enable"
        user={disableUserRowData}
        onConfirm={() =>
          dispatch(enableUser({ client_user_index: disableUserRowData?.client_user_index }))
        }
        onClose={handleCloseEnableUser}
      />
      <CancelSubscriptionDialog
        open={openCancelSubscriptionDialog}
        handleClose={handleCloseCancelSubscription}
        CancelSubscriptionData={CancelSubscriptionRowData}
      />
      <AttachSubscriptionDialog
        open={openAttachSubscriptionDialog}
        handleClose={(message: string) =>
          handleCloseAttachSubscription(message)
        }
        attachSubscriptionData={attachSubscriptionData}
      />
      <ServerMessageSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
      />
    </Grid>
  );
};
export default UserManagement;
